import { Link, useParams } from "react-router-dom";
import { ArrowUpRight } from "@phosphor-icons/react";
import { SiteShell, useSite } from "@/components/site/SiteShell";
import { siteImg, usePageHead } from "@/lib/siteContent";

const hideOnError = (e) => {
  e.currentTarget.style.display = "none";
};

export default function SiteBrand() {
  const site = useSite();
  const { slug } = useParams();
  const brands = site?.config?.brands || [];
  const brand = brands.find((b) => b.slug === slug && b.enabled !== false);

  usePageHead({
    title: brand ? brand.seo_title || brand.name : "Brand",
    description: brand ? brand.seo_description || brand.description || "" : "",
  });

  if (!brand) {
    return (
      <SiteShell site={site} testId="site-brand-missing">
        <main className="px-6 py-28 text-center lg:px-12" data-testid="brand-missing">
          <h1 className="s-display text-3xl sm:text-4xl">Brand not found</h1>
          <p className="mx-auto mt-4 max-w-md text-[color:var(--s-mute)]">
            This collection may have moved. Browse everything we stock instead.
          </p>
          <Link
            to="/collections"
            className="s-btn mt-7 inline-flex"
            data-testid="brand-missing-back"
          >
            Back to collections
          </Link>
        </main>
      </SiteShell>
    );
  }

  const {
    name,
    eyebrow,
    tagline,
    description,
    hero_image: heroImage,
    materials = [],
    features = [],
    gallery = [],
    cta_label: ctaLabel,
    cta_url: ctaUrl,
    cta_external: ctaExternal,
    cta_heading: ctaHeading,
    cta_body: ctaBody,
  } = brand;

  const onImage = !!heroImage;
  const CtaButton = ({ className, testId }) =>
    !ctaLabel ? null : ctaExternal ? (
      <a
        href={ctaUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        data-testid={testId}
      >
        {ctaLabel} <ArrowUpRight size={14} weight="bold" />
      </a>
    ) : (
      <Link to={ctaUrl || "/contact"} className={className} data-testid={testId}>
        {ctaLabel}
      </Link>
    );

  return (
    <SiteShell site={site} testId={`site-brand-${slug}`}>
      {/* Immersive hero */}
      <section className="relative overflow-hidden" data-testid="brand-hero">
        {onImage ? (
          <>
            <img
              src={siteImg(heroImage)}
              alt={name}
              data-testid="brand-hero-image"
              onError={hideOnError}
              referrerPolicy="no-referrer"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/55 to-black/40" />
          </>
        ) : (
          <div className="absolute inset-0 bg-[color:var(--s-ink)]" />
        )}
        <div className="relative mx-auto flex min-h-[70vh] max-w-[2560px] flex-col justify-end px-6 pb-14 pt-32 sm:pb-20 lg:px-12">
          <div className="s-rise max-w-3xl">
            <span className="s-micro inline-flex items-center gap-2 text-white/70">
              <span className="inline-block h-2 w-2 rotate-45 bg-[color:var(--s-gold)]" />
              {eyebrow || "Collections · Brand"}
            </span>
            <h1
              className="s-display mt-5 text-5xl leading-[0.98] text-white sm:text-6xl lg:text-7xl"
              data-testid="brand-headline"
            >
              {name}
            </h1>
            {tagline && (
              <p className="mt-4 text-lg text-white/85 sm:text-xl">{tagline}</p>
            )}
            {description && (
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/70">
                {description}
              </p>
            )}
            <div className="mt-8 flex flex-wrap gap-3">
              <CtaButton className="s-btn" testId="brand-cta-primary" />
              <Link
                to="/contact"
                className="s-ghost-light"
                data-testid="brand-cta-quote"
              >
                Request a quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      <main className="px-6 pb-20 pt-14 sm:pb-28 sm:pt-20 lg:px-12">
        {/* Features */}
        {features.length > 0 && (
          <section className="mx-auto max-w-[2560px]" data-testid="brand-features">
            <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              Why {name}
            </h2>
            <div className="mt-8 grid gap-px overflow-hidden border border-[color:var(--s-line)] bg-[color:var(--s-line)] sm:grid-cols-2 lg:grid-cols-3">
              {features.map((f, i) => (
                <article
                  key={i}
                  data-testid={`brand-feature-${i}`}
                  className="bg-[color:var(--s-surface)] p-7"
                >
                  <span className="s-num text-3xl text-[color:var(--s-gold)]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="s-display mt-4 text-lg">{f.title}</h3>
                  {f.body && (
                    <p className="mt-2 text-sm leading-relaxed text-[color:var(--s-mute)]">
                      {f.body}
                    </p>
                  )}
                </article>
              ))}
            </div>
            {materials.length > 0 && (
              <div
                className="mt-6 flex flex-wrap gap-2"
                data-testid="brand-materials"
              >
                {materials.map((m) => (
                  <span key={m} className="s-pill !h-9 !text-sm">
                    {m}
                  </span>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Collection gallery */}
        {gallery.length > 0 && (
          <section
            className="mx-auto mt-16 max-w-[2560px] sm:mt-24"
            data-testid="brand-gallery"
          >
            <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              The collection
            </h2>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {gallery.map((c, n) => {
                const Card = c.href ? "a" : "div";
                const cardProps = c.href
                  ? { href: c.href, target: "_blank", rel: "noopener noreferrer" }
                  : {};
                return (
                  <Card
                    key={n}
                    {...cardProps}
                    data-testid={`brand-gallery-${n}`}
                    className="s-rise s-card group flex flex-col overflow-hidden !p-0"
                    style={{ animationDelay: `${Math.min(n, 9) * 50}ms` }}
                  >
                    <div className="s-card-fig relative aspect-[4/5] w-full overflow-hidden bg-[color:var(--s-bg)]">
                      <img
                        src={siteImg(c.image)}
                        alt={c.title || name}
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        onError={hideOnError}
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.05]"
                      />
                      {(c.title || c.tag || c.body) && (
                        <span className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
                      )}
                      {c.tag && (
                        <span className="absolute left-4 top-4">
                          <span className="s-pill !h-8 !bg-white/90 !px-3 !text-xs">
                            {c.tag}
                          </span>
                        </span>
                      )}
                      {(c.title || c.body) && (
                        <span className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-5">
                          <span>
                            {c.title && (
                              <span className="s-display block text-xl text-white">
                                {c.title}
                              </span>
                            )}
                            {c.body && (
                              <span className="mt-1 block max-w-[13rem] text-xs leading-snug text-white/80">
                                {c.body}
                              </span>
                            )}
                          </span>
                          {c.href && (
                            <ArrowUpRight
                              size={18}
                              weight="bold"
                              className="shrink-0 text-white/70 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-white"
                            />
                          )}
                        </span>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          </section>
        )}

        {/* Closing CTA */}
        <section
          className="s-warm mt-16 px-6 py-12 text-center sm:mt-24 sm:px-10 sm:py-16"
          data-testid="brand-cta"
        >
          <h2 className="s-display text-3xl sm:text-4xl">
            {ctaHeading || `See ${name} in person`}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-[color:var(--s-mute)]">
            {ctaBody ||
              "Walk our racks, compare the finishes and pick the exact slab for your project. We template, fabricate and install with our own crew."}
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <CtaButton className="s-btn" testId="brand-cta-bottom" />
            <Link to="/inventory" className="s-ghost">
              Browse the inventory
            </Link>
          </div>
        </section>
      </main>
    </SiteShell>
  );
}
